import { memo } from "react";

import { Icon } from "@react/components/Icon";
import { Text } from "@react/components/Text";
import { type FooterSectionSettings } from "@react/sections/Footer/Footer.data";
import { focusRing } from "@react/utilities";
import { COUNTRY_FLAG_ICONS } from "@root/tablescape/constants";
import { CartHeader } from "@sections/Cart/CartHeader";
import { type Localization } from "@sections/DrawerProvider/CountrySelector";
import { type TTailwindString, tw } from "classnames";

const styles = {
  container: tw("bg-background-primary", "h-dynamic-screen"),
  header: tw("flex", "flex-row", "flex-nowrap", "items-center", "h-6"),
  drawerBody: tw("p-6", "overflow-y-auto", "text-primary"),
  mutedText: tw("text-primary-muted"),
  optionsHeader: tw(
    "border",
    "border-0",
    "border-b",
    "border-solid",
    "border-interactive-disabled-pressed",
    "pb-6"
  ),
  optionDisplay: tw("px-4", "flex", "flex-row", "flex-nowrap", "items-center"),
  country: tw("flex-1", "pl-2.5"),
  language: tw("text-char-500"),
  availableOption: tw(
    "w-full",
    "text-left",
    "cursor-pointer",
    "bg-transparent",
    "!px-0" as TTailwindString,
    "py-4",
    "border",
    "border-0",
    "border-b",
    "border-solid",
    "border-interactive-disabled-pressed",
    "no-underline",
    "text-primary"
  ),
  availableOptionLink: tw(
    "flex",
    "flex-row",
    "items-center",
    "flex-1",
    "no-underline",
    "text-primary"
  ),
  selectedOption: tw("mt-4", "mb-8", "py-3", "bg-background-secondary"),
  languagePills: tw(
    "ml-4",
    "flex",
    "flex-row",
    "flex-wrap",
    "gap-2",
    "items-center"
  ),
  languagePill: tw(
    "no-underline",
    "bg-background-primary",
    "text-primary",
    "border",
    "border-solid",
    "border-primary",
    "rounded",
    "px-2",
    "py-1",
    "transition-colors",
    "duration-150",
    "hover:bg-background-secondary" as TTailwindString,
    focusRing
  ),
};

type Props = {
  accessibilityProps: {
    readonly "aria-modal": true;
    readonly ref: (node: HTMLDivElement | null) => void;
    readonly role: "dialog";
    readonly tabIndex: -1;
  };
  closeSelector: () => void;
  localization: Localization;
  localizationOptions: Localization[];
  text: FooterSectionSettings["countrySelector"]["text"];
};

const CountrySelectorViewBase = ({
  accessibilityProps,
  closeSelector,
  localization,
  localizationOptions,
  text,
}: Props) => {
  const localizationOptionGenerator = (option: Localization) => {
    return (
      <>
        <Icon name={COUNTRY_FLAG_ICONS[option.isoCode]} />
        <Text as="body1" className={styles.country} size={{ md: "md" }}>
          {option.country}
        </Text>
        <Text as="body1" className={styles.language} size={{ md: "md" }}>
          {option.languages[0].name}
        </Text>
      </>
    );
  };
  return (
    <div className={styles.container} {...accessibilityProps}>
      <CartHeader handleCloseDrawer={closeSelector}>{text.header}</CartHeader>

      <div className={styles.drawerBody}>
        <Text className={styles.mutedText}>{text.location}</Text>
        {localization?.isoCode && (
          <div className={tw(styles.selectedOption, styles.optionDisplay)}>
            {localizationOptionGenerator(localization)}
          </div>
        )}
        <div className={styles.optionsHeader}>
          <Text as="body0">{text.label}</Text>
          <Text className={styles.mutedText}>{text.shipping}</Text>
        </div>
        {localizationOptions
          .sort((a, b) => a.country.localeCompare(b.country))
          .map((option: Localization) => (
            <div
              className={tw(styles.availableOption, styles.optionDisplay)}
              key={option.country}
            >
              <a
                aria-label={`${option.country} - ${option.languages[0].name}`}
                className={tw(styles.availableOptionLink)}
                href={`https://${option.languages[0].domain}`}
              >
                <Icon name={COUNTRY_FLAG_ICONS[option.isoCode]} />
                <Text as="body1" className={styles.country} size={{ md: "md" }}>
                  {option.country}
                </Text>
              </a>
              <div className={styles.languagePills}>
                {option.languages.map((lang) => (
                  <a
                    aria-label={`${option.country} - ${lang.name}`}
                    className={styles.languagePill}
                    href={`https://${lang.domain}`}
                    key={`${option.isoCode}-${lang.value}`}
                  >
                    <Text as="body1" size={{ md: "md" }}>
                      {lang.name}
                    </Text>
                  </a>
                ))}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export const CountrySelectorView = memo(CountrySelectorViewBase);
